import React, { useContext, useEffect, useState } from 'react';
import PageLayout from '../pagelayout';
import translations from './addpracticeplace.i18n';
import { useIntl } from 'react-intl';
import { Grid } from '@mui/material';
import PanelHeader from '../components/panelheader/panelHeader';
import { ToolbarLink } from '../pagelayout/toolbarLink';
import Button, { ButtonVariant } from '../components/button';
import { TextInput } from '../components/input/textInput';
import { NumberInput } from '../components/input/numberInput';
import Autocomplete from '../components/input/autocomplete';
import { StudiestedApiContext } from '../api/StudiestedApiContext';
import { useLoader } from '../api/useLoader';
import Spinner from '../components/spinner/spinner';
import DatePicker from '../components/datepicker';
import { parseIsoTzLess } from '../date.utils';
import * as css from './addplace.module.scss';
import classNames from 'classnames';
import {
  AlpKommuneDto,
  AlpLandDto,
  AlpPraksisstedMedNivaaDto,
  HttpError,
  MatchingPraksisstedDto,
  PraksisstedtypeDto,
} from '../api/generated/studiested-generated-api';
import { validateOrgnr } from '../util';
import {
  AlpNotificationType,
  NotificationFunctionContext,
} from '../components/notifications/notificationProvider';
import { uuidv4 } from '../util/uuidv4';
import { IndentMuligPraksissted } from '../components/indentation/indentation';
import { InformationDialog } from '../components/dialog/informationDialog';
import { StedType } from '../components/hierarchy/onhoverhierarchyinfo';
import { validatePostnr } from '../util/validatePostnr';
import validatePhoneNo from '../util/validatePhoneNo';
import { useNavigate } from 'react-router-dom';

const InvalidOrgNr: React.FC<
  React.PropsWithChildren<{
    notFoundInEnhetsreg?: boolean;
  }>
> = ({ notFoundInEnhetsreg }) => {
  const { formatMessage } = useIntl();
  const invalidOrgnr = formatMessage({ id: translations.invalidOrgnr.id });
  const notInEnhetsregText = formatMessage({
    id: translations.notInEnhetsreg.id,
  });

  return (
    <Grid item={true} xs={12}>
      <p data-cy="invalid-orgnr">
        <strong>{`${invalidOrgnr} ${
          notFoundInEnhetsreg ? notInEnhetsregText : ''
        }`}</strong>
      </p>
    </Grid>
  );
};

const AddPracticePlacePage = () => {
  const { formatMessage } = useIntl();

  const [orgnr, setOrgnr] = useState<number | undefined>();

  const labels = {
    title: formatMessage({ id: translations.title.id }),
    subtitle: formatMessage({ id: translations.subtitle.id }),
    organisasjontitle: formatMessage({ id: translations.organisasjontitle.id }),
    organisasjonsubtitle: formatMessage({
      id: translations.organisasjonsubtitle.id,
    }),
    cancel: formatMessage({ id: translations.cancel.id }),
    orgnr: formatMessage({ id: translations.orgnr.id }),
  };

  const actionItems = ToolbarLink({
    title: labels.cancel,
    exact: true,
    path: '/practiceplaces',
    icon: null,
    buttonVariant: ButtonVariant.Tertiary,
  });

  return (
    <>
      <PageLayout
        isIndented
        title={labels.title}
        subtitle={labels.subtitle}
        actionItems={actionItems}
      >
        <Grid container={true} spacing={3}>
          <Grid item={true} xs={12}>
            <PanelHeader
              title={labels.organisasjontitle}
              subtitle={labels.organisasjonsubtitle}
            />
          </Grid>
          <Grid item={true} xs={4}>
            <NumberInput
              htmlId={'orgnr'}
              label={labels.orgnr}
              value={orgnr}
              onChange={setOrgnr}
            />
          </Grid>
          <Grid item={true} xs={8} />
          {orgnr?.toString().length === 9 &&
            (validateOrgnr(orgnr.toString()) ? (
              <NewPracticePlaceInfo orgnr={orgnr} />
            ) : (
              <InvalidOrgNr />
            ))}
        </Grid>
      </PageLayout>
    </>
  );
};

const PadPraksisstedtypeWithKode: React.FC<
  React.PropsWithChildren<{
    praksisstedtype: PraksisstedtypeDto;
  }>
> = ({ praksisstedtype }) => (
  <>
    <span className={css.praksisstedtypekodePadding}>
      {praksisstedtype.praksisstedtypekode}
    </span>
    <span className={css.praksisstedtypekodeNavn}>
      {praksisstedtype.praksisstedtypenavn}
    </span>
  </>
);

const FoundMatchConfirmation: React.FC<
  React.PropsWithChildren<{
    matchingPlaces: MatchingPraksisstedDto[];
    isOpen: boolean;
    onClose: () => void;
  }>
> = ({ matchingPlaces, isOpen, onClose }) => {
  const { formatMessage } = useIntl();

  const labels = {
    matchPraksisstedText: formatMessage({
      id: translations.matchPraksisstedText.id,
    }),
    matchPraksisstedSubtext: formatMessage({
      id: translations.matchPraksisstedSubtext.id,
    }),
    modalNavn: formatMessage({
      id: translations.modalNavn.id,
    }),
    modalAdresse: formatMessage({
      id: translations.modalAdresse.id,
    }),
    modalPostnr: formatMessage({
      id: translations.modalPostnr.id,
    }),
  };

  return (
    <InformationDialog
      isOpen={isOpen}
      title={labels.matchPraksisstedText}
      subtitle={labels.matchPraksisstedSubtext}
      onClose={onClose}
    >
      <ul className={css.duplikatPraksisstedList}>
        {matchingPlaces.map((sted) => (
          <>
            <li key={sted.navn}>
              <p>{`${labels.modalNavn} ${sted.navn}`}</p>
              <p>{`${labels.modalAdresse} ${sted.adresse}`}</p>
              <p>{`${labels.modalPostnr} ${sted.postnr}`}</p>
            </li>
            <hr />
          </>
        ))}
      </ul>
    </InformationDialog>
  );
};

export const NewPracticePlaceInfo: React.FC<
  React.PropsWithChildren<{ orgnr: number }>
> = ({ orgnr }) => {
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const [dateFromIso, setDateFromIso] = useState<string | undefined>();
  const [navnBokmaal, setNavnBokmaal] = useState('');
  const [navnNynorsk, setNavnNynorsk] = useState('');
  const [navnEngelsk, setNavnEngelsk] = useState('');
  const [adresseLinje1, setAdresseLinje1] = useState('');
  const [adresseLinje2, setAdresseLinje2] = useState('');
  const [postnr, setPostnr] = useState('');
  const [poststed, setPoststed] = useState('');
  const [telefonLand, setTelefonLand] = useState('47');
  const [telefon, setTelefon] = useState('');
  const [epost, setEpost] = useState('');
  const [url, setUrl] = useState('');
  const [eier, setEier] = useState<AlpPraksisstedMedNivaaDto>();
  const [adresseLand, setAdresseLand] = useState<AlpLandDto>();
  const [kommune, setKommune] = useState<AlpKommuneDto>();
  const [autofillable, setAutofillable] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [valgtPraksisstedtyper, setValgtPraksisstedtyper] = useState<
    PraksisstedtypeDto[]
  >([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showHelpText, setShowHelpText] = useState(false);
  const [matchingPlaces, setMatchingPlaces] = useState<
    MatchingPraksisstedDto[]
  >([]);

  const { praksisstedApi } = useContext(StudiestedApiContext);
  const { addNotification } = useContext(NotificationFunctionContext);
  const {
    data: registeringInformasjon,
    loading: loadingRegistreringsInformasjon,
  } = useLoader(
    () => praksisstedApi.fsPraksisstedGet({ queryParams: { orgnr: orgnr } }),
    [orgnr],
  );

  const lookup = () => {
    navnBokmaal &&
      praksisstedApi
        .matchingPraksisstederGet({
          queryParams: {
            navn: navnBokmaal,
          },
        })
        .then((data) => {
          setMatchingPlaces(data);
          data.length > 0 && setShowHelpText(true);
        });
  };

  const { data: praksisstedtyper, loading: loadingPraksisstedtyper } =
    useLoader(() => praksisstedApi.fsPraksisstedtypeGet({}), []);

  const { data: enhetsRegisteret } = useLoader(
    () =>
      praksisstedApi.enhetsregPraksisstedOrgnrGet({ pathParams: { orgnr } }),
    [orgnr],
  );

  const labels = {
    title: formatMessage({ id: translations.title.id }),
    subtitle: formatMessage({ id: translations.subtitle.id }),
    praksisstedtitle: formatMessage({ id: translations.praksisstedtitle.id }),
    praksisstedsubtitle: formatMessage({
      id: translations.praksisstedsubtitle.id,
    }),
    ukjentOrgnr: formatMessage(
      { id: translations.ukjentOrgnr.id },
      { orgnr: orgnr },
    ),
    kjentOrgnr: formatMessage(
      { id: translations.kjentOrgnr.id },
      { orgnr: orgnr },
    ),
    cancel: formatMessage({ id: translations.cancel.id }),
    orgnr: formatMessage({ id: translations.orgnr.id }),
    eier: formatMessage({ id: translations.eier.id }),
    aktivDato: formatMessage({ id: translations.aktivDato.id }),
    navnbokmaal: formatMessage({ id: translations.navnbokmaal.id }),
    navnnynorsk: formatMessage({ id: translations.navnnynorsk.id }),
    navnengelsk: formatMessage({ id: translations.navnengelsk.id }),
    adresseLinje1: formatMessage({ id: translations.adresselinje1.id }),
    adresseLinje2: formatMessage({ id: translations.adresselinje2.id }),
    postnr: formatMessage({ id: translations.postnr.id }),
    poststed: formatMessage({ id: translations.poststed.id }),
    adresseLand: formatMessage({ id: translations.adresseLand.id }),
    kommune: formatMessage({ id: translations.kommune.id }),
    telefonLand: formatMessage({ id: translations.telefonLand.id }),
    telefon: formatMessage({ id: translations.telefon.id }),
    epost: formatMessage({ id: translations.epost.id }),
    url: formatMessage({ id: translations.url.id }),
    save: formatMessage({ id: translations.save.id }),
    isSaving: formatMessage({ id: translations.isSaving.id }),
    saveSuccess: formatMessage({ id: translations.saveSuccess.id }),
    besoksAdresse: formatMessage({ id: translations.besoksAdresse.id }),
    kontaktInfo: formatMessage({ id: translations.kontaktInfo.id }),
    autofillPossible: formatMessage({ id: translations.autofillPossible.id }),
    autofillBtn: formatMessage({ id: translations.autofillBtn.id }),
    praksisstedMatchFound: formatMessage({
      id: translations.praksisstedMatchFound.id,
    }),
    praksisstedDuplikater: formatMessage({
      id: translations.praksisstedDuplikater.id,
    }),
    praksisstedtypekoder: formatMessage({
      id: translations.praksisstedtypekoder.id,
    }),
  };

  const saveEnabled = () => {
    const adresse =
      adresseLinje1.length > 0 &&
      poststed.length > 0 &&
      validatePostnr(postnr) &&
      kommune;
    const ferdigLastetPraksisstedtyper =
      praksisstedtyper &&
      praksisstedtyper.praksisstedertype &&
      praksisstedtyper.praksisstedertype.length > 0;
    let hasOwner =
      registeringInformasjon &&
      registeringInformasjon.praksissteder.length > 0 &&
      eier;
    let hasOrgNumber =
      registeringInformasjon &&
      registeringInformasjon.praksissteder.length === 0 &&
      orgnr.toString().length === 9;
    let validTelefonData =
      !telefon ||
      (telefonLand && telefon && validatePhoneNo(telefonLand + telefon));
    return (
      !isSaving &&
      navnBokmaal.length > 0 &&
      adresse &&
      ferdigLastetPraksisstedtyper &&
      valgtPraksisstedtyper &&
      validTelefonData &&
      (hasOwner || hasOrgNumber)
    );
  };

  const save = () => {
    setIsSaving(true);
    praksisstedApi
      .fsPraksisstedPost({
        alpPraksisstedPostDto: {
          orgnr: eier ? undefined : orgnr,
          praksisstedkodeEier: eier ? eier.praksisstedkode : undefined,
          praksisstednavnBokmal: navnBokmaal,
          praksisstednavnNynorsk: navnNynorsk,
          praksisstednavnEngelsk: navnEngelsk,
          praksisstedtyper: valgtPraksisstedtyper,
          adrlin1: adresseLinje1,
          adrlin2: adresseLinje2,
          adrlin3: poststed,
          postnr: postnr ? postnr : undefined,
          adresseland: adresseLand?.landnavnNorsk,
          landnr: adresseLand
            ? adresseLand.landnr
            : registeringInformasjon?.land.find(
                (land) => land.landnavnNorsk.toUpperCase() === 'NORGE',
              )?.landnr,
          telefonlandnr:
            telefon && telefonLand ? telefonLand.toString() : undefined,
          telefonnr: telefon,
          emailadresse: epost,
          fylkesnr: kommune?.fylkesnr,
          kommunenr: kommune?.kommunenr,
          url: url,
          datoFra: dateFromIso
            ? parseIsoTzLess(dateFromIso).getTime()
            : Date.now(),
        },
      })
      .then(() => {
        addNotification({
          id: uuidv4(),
          type: AlpNotificationType.SUCCESS,
          message: labels.saveSuccess,
        });
        navigate('/practiceplaces', { replace: true });
      })
      .catch((err: HttpError) => {
        addNotification({
          id: uuidv4(),
          type: AlpNotificationType.ERROR,
          message: err.body,
        });
      })
      .finally(() => setIsSaving(false));
  };

  const defaultCountry = registeringInformasjon?.land.find(
    (land) => land.landnavnNorsk.toUpperCase() === 'NORGE',
  );

  const getEregKommuneInData = () =>
    registeringInformasjon?.kommuner.find(
      (kommune) =>
        kommune.kommunenavn.toLowerCase() ===
        enhetsRegisteret?.forretningsadresse?.kommune?.toLowerCase(),
    );

  const getEregLandInData = () =>
    registeringInformasjon?.land.find(
      (land) =>
        land.landnavnNorsk.toLowerCase() ===
        enhetsRegisteret?.forretningsadresse?.land?.toLowerCase(),
    );

  const getSelectedKommuneAsOption = () => {
    if (kommune) {
      return {
        id: `${kommune.kommunenr}-${kommune.fylkesnr}`,
        navn: kommune.kommunenavn,
      };
    }
  };

  const getSelectedLandAsOption = () => {
    if (adresseLand) {
      return { id: `${adresseLand.landnr}`, navn: adresseLand.landnavnNorsk };
    }
  };

  const autofillBtnHandler = () => {
    const { navn, forretningsadresse, hjemmeside, maalform } =
      enhetsRegisteret!;

    if (navn) {
      setNavnBokmaal(navn);
      maalform === 'Nynorsk' && setNavnNynorsk(navn);
    }
    if (forretningsadresse) {
      const { adresse, postnummer, poststed } = forretningsadresse;

      adresse && setAdresseLinje1(adresse);
      postnummer && setPostnr(postnummer);
      poststed && setPoststed(poststed);
    }

    const kommune = getEregKommuneInData();
    const land = getEregLandInData();

    kommune && setKommune(kommune);
    land && setAdresseLand(land);
    hjemmeside && setUrl(hjemmeside);
  };

  useEffect(() => {
    setAutofillable(
      !!enhetsRegisteret &&
        Object.values(enhetsRegisteret).some((value) => value),
    );
  }, [enhetsRegisteret]);

  const onChangePostnr = (value: string) => {
    setPostnr(value.trim());
  };

  return (
    <>
      {loadingRegistreringsInformasjon || loadingPraksisstedtyper ? (
        <Grid item={true} xs={12}>
          <Spinner />
        </Grid>
      ) : registeringInformasjon &&
        praksisstedtyper &&
        (enhetsRegisteret!! ||
          registeringInformasjon.praksissteder.length > 0) ? (
        <>
          <Grid item={true} xs={12}>
            <PanelHeader
              title={labels.praksisstedtitle}
              subtitle={labels.praksisstedsubtitle}
            />
          </Grid>
          {registeringInformasjon.praksissteder.length ? (
            <>
              <Grid item={true} xs={4}>
                <Autocomplete
                  id={'organisasjon'}
                  label={labels.eier}
                  required
                  options={
                    registeringInformasjon &&
                    registeringInformasjon.praksissteder &&
                    registeringInformasjon.praksissteder.map((praksissted) => ({
                      ...praksissted,
                      id: praksissted.id,
                      navn: praksissted.praksisstednavnBokmal,
                      dybde: praksissted.level,
                      onHoverType: StedType.PRAKSISSTED,
                    }))
                  }
                  renderOption={(option) => (
                    <IndentMuligPraksissted praksissted={option} />
                  )}
                  onChange={(
                    event: object,
                    value: AlpPraksisstedMedNivaaDto,
                  ) => {
                    setEier(value);
                  }}
                />
              </Grid>
              <Grid item={true} xs={8} />
              <Grid item={true} xs={12}>
                <p data-cy="kjent-orgnr">
                  <strong>{labels.kjentOrgnr}</strong>
                </p>
              </Grid>
            </>
          ) : (
            <>
              <Grid item={true} xs={12}>
                <p>
                  <span>
                    <strong>{labels.ukjentOrgnr}</strong>
                    {autofillable && (
                      <>
                        <br />
                        <strong>{labels.autofillPossible}</strong>
                      </>
                    )}
                  </span>
                </p>
              </Grid>
              {autofillable && (
                <Grid xs={12} item>
                  <Button.Secondary
                    testSelector={'autofill-btn'}
                    onClick={autofillBtnHandler}
                  >
                    {labels.autofillBtn}
                  </Button.Secondary>
                </Grid>
              )}
            </>
          )}
          <Grid item={true} xs={9}>
            {praksisstedtyper.praksisstedertype && (
              <Autocomplete
                id={'Praksisstedtypekoder'}
                label={labels.praksisstedtypekoder}
                multiple
                onChange={(
                  event: object,
                  value: { id: string; name: string }[],
                ) => {
                  setValgtPraksisstedtyper(
                    value.map((it) => ({
                      praksisstedtypekode: it.id,
                      praksisstedtypenavn: it.name,
                    })),
                  );
                }}
                options={[
                  ...praksisstedtyper.praksisstedertype.map((it) => ({
                    id: it.praksisstedtypekode!,
                    navn: it.praksisstedtypekode!, // Mainly used for the presentation of selected values
                    customSearchString:
                      it.praksisstedtypekode! + it.praksisstedtypenavn!,
                    skjultNavn: it.praksisstedtypenavn!, // Invisible to the Autocomplete component (passed through)
                  })),
                ]}
                renderOption={(option) => (
                  <PadPraksisstedtypeWithKode
                    praksisstedtype={{
                      praksisstedtypekode: option.id,
                      praksisstedtypenavn: option.skjultNavn,
                    }}
                  />
                )}
              />
            )}
          </Grid>
          <Grid xs={3} />
          <Grid item={true} xs={2}>
            <DatePicker
              value={dateFromIso}
              onChange={setDateFromIso}
              label={labels.aktivDato}
            />
          </Grid>
          <Grid item={true} xs={10} />
          <Grid item={true} xs={3}>
            <TextInput
              htmlId={'navnBokmaal'}
              required
              label={labels.navnbokmaal}
              value={navnBokmaal}
              onChange={setNavnBokmaal}
              autoComplete={'off'}
              onBlur={() => lookup()}
              helpText={
                showHelpText &&
                navnBokmaal &&
                matchingPlaces.length > 0 &&
                labels.praksisstedMatchFound
              }
              helpTextRole={'alert'}
            />
          </Grid>
          <Grid item={true} xs={3}>
            {navnBokmaal && matchingPlaces.length > 0 && (
              <div className={css.checkStedButton}>
                <Button.Secondary
                  id={'sjekkPraksissteder'}
                  tabIndex={0}
                  disabled={!navnBokmaal}
                  onClick={() => setIsModalOpen(true)}
                >
                  {labels.praksisstedDuplikater}
                </Button.Secondary>
              </div>
            )}
          </Grid>
          <Grid xs={12} />
          <Grid item={true} xs={3}>
            <TextInput
              htmlId={'navnNynorsk'}
              label={labels.navnnynorsk}
              value={navnNynorsk}
              onChange={setNavnNynorsk}
              autoComplete={'off'}
            />
          </Grid>
          <Grid item={true} xs={3}>
            <TextInput
              htmlId={'navnEngelsk'}
              label={labels.navnengelsk}
              value={navnEngelsk}
              onChange={setNavnEngelsk}
              autoComplete={'off'}
            />
          </Grid>
          <Grid item={true} xs={3} />
          <Grid item={true} xs={12}>
            <PanelHeader title={labels.besoksAdresse} />
          </Grid>
          <Grid item={true} xs={3}>
            <TextInput
              htmlId={'adresseLinje1'}
              required
              label={labels.adresseLinje1}
              value={adresseLinje1}
              onChange={setAdresseLinje1}
              autoComplete={'off'}
            />
          </Grid>
          <Grid item={true} xs={3}>
            <TextInput
              htmlId={'adresseLinje2'}
              label={labels.adresseLinje2}
              value={adresseLinje2}
              onChange={setAdresseLinje2}
              autoComplete={'off'}
            />
          </Grid>
          <Grid item={true} xs={3}>
            <Autocomplete
              id={'kommune'}
              label={labels.kommune}
              searchThreshold={2}
              required
              options={
                registeringInformasjon &&
                registeringInformasjon.kommuner &&
                registeringInformasjon.kommuner.map((kommune) => ({
                  ...kommune,
                  id: `${kommune.kommunenr}-${kommune.fylkesnr}`,
                  navn: kommune.kommunenavn,
                  dybde: -1,
                }))
              }
              selectedOption={getSelectedKommuneAsOption()}
              onChange={(event: object, value: AlpKommuneDto) => {
                setKommune(value);
              }}
            />
          </Grid>
          <Grid item={true} xs={3} />
          <Grid item={true} xs={2}>
            <TextInput
              htmlId={'postnr'}
              required
              label={labels.postnr}
              value={postnr}
              onChange={onChangePostnr}
            />
          </Grid>
          <Grid item={true} xs={4}>
            <TextInput
              htmlId={'poststed'}
              required
              label={labels.poststed}
              value={poststed}
              onChange={setPoststed}
              autoComplete={'off'}
            />
          </Grid>
          <Grid item={true} xs={3}>
            <Autocomplete
              id={'land'}
              label={labels.adresseLand}
              searchThreshold={2}
              options={
                registeringInformasjon.land &&
                registeringInformasjon.land.map((land) => ({
                  ...land,
                  id: `${land.landnr}`,
                  navn: land.landnavnNorsk,
                }))
              }
              defaultValue={
                defaultCountry && {
                  ...defaultCountry,
                  id: `${defaultCountry.landnr}`,
                  navn: defaultCountry.landnavnNorsk,
                }
              }
              selectedOption={getSelectedLandAsOption()}
              onChange={(event: object, value: AlpLandDto) => {
                setAdresseLand(value);
                setTelefonLand(value.telefonlandnr || '');
              }}
              data-cy={'landInput'}
            />
          </Grid>
          <Grid item={true} xs={3} />
          <Grid item={true} xs={12}>
            <PanelHeader title={labels.kontaktInfo} />
          </Grid>
          <Grid item={true} xs={1}>
            <TextInput
              htmlId={'telefonLand'}
              label={labels.telefonLand}
              value={telefonLand}
              onChange={setTelefonLand}
              data-cy={'telefonLand'}
            />
          </Grid>
          <Grid item={true} xs={2}>
            <TextInput
              htmlId={'telefon'}
              label={labels.telefon}
              value={telefon}
              onChange={setTelefon}
              autoComplete={'off'}
            />
          </Grid>
          <Grid item={true} xs={9} />
          <Grid item={true} xs={3}>
            <TextInput
              htmlId={'epost'}
              label={labels.epost}
              value={epost}
              onChange={setEpost}
              autoComplete={'off'}
            />
          </Grid>
          <Grid item={true} xs={3}>
            <TextInput
              htmlId={'url'}
              label={labels.url}
              value={url}
              onChange={setUrl}
              autoComplete={'off'}
            />
          </Grid>
          <Grid item={true} xs={6} />
          <Grid item={true} xs={6} />
          <Grid item={true} xs={9}>
            <div className={classNames(css.buttonContainer, css.right)}>
              <Button.Secondary
                disabled={!saveEnabled()}
                onClick={() => save()}
              >
                {isSaving ? labels.isSaving : labels.save}
              </Button.Secondary>
            </div>
          </Grid>
          <Grid item={true} xs={3} />
          <FoundMatchConfirmation
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            matchingPlaces={matchingPlaces}
          />
        </>
      ) : (
        <InvalidOrgNr notFoundInEnhetsreg />
      )}
    </>
  );
};

export default AddPracticePlacePage;
